import { apiService } from "../api";
import { Product, ProductImage, ProductVariant, ProductSpecification } from "../productService";

// Image data for creating/updating products
export interface ProductImageInput {
  id?: string;
  imageData?: string; // base64 encoded image (for new uploads)
  imageUrl?: string; // URL for existing images (when editing)
  displayOrder?: number;
  altText?: string | null;
}

export interface CreateProductRequest {
  categoryId: string;
  name: string;
  description?: string | null;
  basePrice: number;
  originalPrice?: number | null;
  discountPercentage?: number | null;
  rating?: number | null;
  ratingCount?: number | null;
  isActive?: boolean;
  inStock?: boolean;
  images?: ProductImageInput[]; // Use ProductImageInput for creation (base64)
  variants?: ProductVariant[];
  specifications?: ProductSpecification[];
}

export interface UpdateProductRequest {
  categoryId?: string;
  name?: string;
  description?: string | null;
  basePrice?: number;
  originalPrice?: number | null;
  discountPercentage?: number | null;
  rating?: number | null;
  ratingCount?: number | null;
  isActive?: boolean;
  inStock?: boolean;
  images?: ProductImageInput[]; // Use ProductImageInput for updates (base64)
  variants?: ProductVariant[];
  specifications?: ProductSpecification[];
}

export interface ProductsResponse {
  products: Product[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export const adminProductService = {
  getAll: async (page: number = 1, limit: number = 20): Promise<ProductsResponse> => {
    const response = await apiService.get<ProductsResponse>(
      `/admin/products?page=${page}&limit=${limit}`
    );
    // Normalize products to convert string decimals to numbers
    return {
      ...response,
      products: response.products.map((product) => ({
        ...product,
        basePrice: Number(product.basePrice),
        originalPrice: product.originalPrice ? Number(product.originalPrice) : null,
        discountPercentage: product.discountPercentage
          ? Number(product.discountPercentage)
          : null,
        rating: product.rating ? Number(product.rating) : null,
        ratingCount: product.ratingCount ? Number(product.ratingCount) : null,
        variants:
          product.variants?.map((v) => ({
            ...v,
            priceAdjustment: Number(v.priceAdjustment),
          })) || [],
      })),
    };
  },

  getById: async (id: string): Promise<Product> => {
    const product = await apiService.get<Product>(`/admin/products/${id}`);
    return {
      ...product,
      basePrice: Number(product.basePrice),
      originalPrice: product.originalPrice ? Number(product.originalPrice) : null,
      discountPercentage: product.discountPercentage
        ? Number(product.discountPercentage)
        : null,
      rating: product.rating ? Number(product.rating) : null,
      ratingCount: product.ratingCount ? Number(product.ratingCount) : null,
      variants:
        product.variants?.map((v) => ({
          ...v,
          priceAdjustment: Number(v.priceAdjustment),
        })) || [],
    };
  },

  create: async (data: CreateProductRequest): Promise<Product> => {
    return await apiService.post<Product>("/admin/products", data);
  },

  update: async (id: string, data: UpdateProductRequest): Promise<Product> => {
    return await apiService.put<Product>(`/admin/products/${id}`, data);
  },

  delete: async (id: string): Promise<void> => {
    return await apiService.delete(`/admin/products/${id}`);
  },
};

